import { TargetCouponDiscount, TargetCouponSubtraction } from '@/types/coupon';
import { isValidArray } from '@/utils/isValidArray';
import Constants from '@/constants/const';

export const formatPercentageCouponValue = (percentage: number, price: number) => {
  if (percentage == null) return null;
  if (price == null) return null;
  const discountAmount = Math.floor(((price * percentage) / 100) * 100) / 100;
  return discountAmount.toFixed(2);
};

/**
 * 根据 benefits接口返回的coupon信息，获取目标产品的coupon信息
 * @param discounts
 * @param productId
 * @returns
 */
export const getTargetProductDiscountInfo = (
  discounts: TargetCouponDiscount[] | undefined,
  productId: number | undefined
) => {
  if (!isValidArray(discounts) || productId == null) return null;
  const idNum = Number(productId);
  if (!Number.isFinite(idNum)) return null;
  return discounts.find(item => Number(item.product_id) === idNum) || null;
};

/**
 * 格式化目标优惠券文案
 * @param template 模板
 * @param subtraction 优惠券减免信息
 * @returns 格式化后的文案
 */
export const formatPersonalizedCouponCopyDesc = (
  template: string | undefined,
  subtraction?: TargetCouponSubtraction | null,
  price?: number
): string => {
  if (template == null || template === '' || !subtraction) return '';
  const { type, reduce, percentage } = subtraction || {};
  if (!type) return '';

  // 类型 D：固定金额减免
  if (type === 'D') {
    const value = reduce != null ? String(reduce) : '';
    return template.replace(/%s/g, value);
  }

  if (type === 'Z') {
    if (percentage == null) return '';

    // 如果有price，则转换成金额
    if (price) {
      const formatted = formatPercentageCouponValue(percentage, price);
      return template.replace(/%s/g, formatted ?? '');
    }
    // 如果没有 price，转换成百分比 (注意：这里需要将$符号一起替换)
    return template.replace(/\$%s/g, `${percentage}% off`);
  }

  return '';
};

/**
 * 格式化目标优惠券折扣信息
 * @param discount 优惠券折扣信息
 * @returns 格式化后的折扣信息
 */
export const formatPersonalizedDiscountCopy = (
  discount: Pick<TargetCouponDiscount, 'copywriting' | 'claimed' | 'subtraction'> | null | undefined,
  price?: number
): string => {
  if (!discount) return '';
  const template = discount.claimed ? discount.copywriting?.claim_desc : discount.copywriting?.unclaim_desc;
  return formatPersonalizedCouponCopyDesc(template, discount.subtraction, price);
};

/**
 * 个性化券领取条：当前 URL（去掉语言前缀后的 path）中**包含**任一下列关键路径片段即展示。
 * 首页单独放行 `/`。
 */
const PRODUCT_CLAIMED_PATH_KEYWORDS: readonly string[] | undefined = undefined;

const PRODUCT_CLAIMED_BLACKLIST: readonly string[] | undefined = undefined;

export const normalizePathNoLang = (asPath: string): string => {
  const pathOnly = asPath.split('?')[0].split('#')[0];
  const stripped = pathOnly.replace(Constants.LANGUAGE_REGEXP_TWO, '');
  const withSlash = stripped.startsWith('/') ? stripped : stripped ? `/${stripped}` : '/';
  return withSlash || '/';
};

export const isProductClaimedUrlAllowed = (pathNoLang: string, type: 'whitelist' | 'blacklist'): boolean => {
  // 1. 处理白名单逻辑
  if (type === 'whitelist') {
    // 首页通常是特殊放行的
    if (pathNoLang === '/') return true;
    return PRODUCT_CLAIMED_PATH_KEYWORDS?.some(keyword => pathNoLang.includes(keyword));
  }

  // 2. 处理黑名单逻辑
  // 逻辑：只要【不在】黑名单数组中，就返回 true
  return !PRODUCT_CLAIMED_BLACKLIST?.some(keyword => pathNoLang.includes(keyword));
};
